import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Flag, Mail, Lock, CheckCircle, ChevronLeft} from 'lucide-react';
import { auth } from '../../config/firebase';
import { db } from '../../config/firebase';
import { updateProfile } from 'firebase/auth';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

function Profile() {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  const getBackgroundColor = (name) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-orange-500',
      'bg-teal-500',
      'bg-red-500'
    ];

    if (!name) return colors[0]; // Default color if no name

    const charCode = name.charAt(0).charCodeAt(0);
    return colors[charCode % colors.length];
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        console.log("Current user:", user);
        console.log("Display name:", user.displayName);
        setEmail(user.email);
        setDisplayName(user.displayName || '');
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = auth.currentUser;
      if (user) {
        await updateProfile(user, {
          displayName: displayName
        });
        console.log("Display name updated:", displayName); // Debug log
        setSuccessMessage('Profile updated successfully!');
      }
    } catch (error) {
      setError('Failed to update profile');
      console.error("Update error:", error);
    }
    setIsLoading(false);
  };

  const setUserUnavailableOnLogout = async (userId) => {
    try {
      // Get all groups where the user is a member
      const groupsQuery = query(
        collection(db, 'groups'),
        where(`members.${userId}`, '!=', null)
      );
      
      const groupsSnapshot = await getDocs(groupsQuery);
      
      if (groupsSnapshot.empty) {
        console.log('No groups found for the user');
        return;
      }
      
      console.log(`Found ${groupsSnapshot.docs.length} groups to update`);
      
      // Update each group
      const updatePromises = groupsSnapshot.docs.map(groupDoc => {
        console.log(`Updating status in group: ${groupDoc.id}`);
        return updateDoc(doc(db, 'groups', groupDoc.id), {
          [`members.${userId}.status`]: false,
          [`members.${userId}.lastUpdated`]: new Date()
        });
      });
      
      await Promise.all(updatePromises);
      console.log('Updated status to unavailable in all groups');
    } catch (error) {
      console.error('Error updating status on logout:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      const userId = auth.currentUser?.uid;
      
      if (userId) {
        console.log('Setting user unavailable before logout');
        // Set user unavailable in all groups before logging out
        await setUserUnavailableOnLogout(userId);
      }
      
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      setError('Failed to sign out');
      console.error(error);
    }
  };

  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .slice(0, 3) // Limit to first 3 initials
      .join('')
      .toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto pt-10">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6 text-gray-600 hover:text-gray-900 -ml-2 flex items-center"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to Dashboard
        </Button>

        <Card className="w-full">
          <CardHeader className="space-y-4 text-center">
            {/* Avatar */}
            <div className="flex justify-center">
              <div className={`w-20 h-20 rounded-full ${getBackgroundColor(displayName)} flex items-center justify-center`}>
                <span className="text-2xl font-medium text-white">
                  {getInitials(displayName)}
                </span>
              </div>
            </div>
            <CardTitle className="text-2xl">Profile Settings</CardTitle>
          </CardHeader>

          <CardContent className="space-y-8 mb-8">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl">
                {error}
              </div>
            )}
            {successMessage && (
              <div className="p-3 bg-green-50 border border-green-200 text-green-600 text-sm rounded-xl">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <p className="text-green-700 font-medium">{successMessage}</p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSave}>
              {/* Display Name Field */}
              <div className="space-y-4">
                <label className="block text-left text-sm font-medium text-gray-600">
                  Display Name
                </label>
                <Input
                  type="text"
                  value={displayName}
                  placeholder="Bernie Madoff"
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl text-left px-3 py-3 mb-2"
                />
              </div>

              {/* Email Field */}
              <div> 
                <label className="block text-gray-600 text-sm text-left mt-4 mb-2">
                  Email
                </label>
                <Input
                  type="email"
                  value={email || ''} // Add fallback empty string
                  disabled
                  className="w-full border border-gray-200 rounded-xl text-left bg-gray-50 px-3 py-3 mb-1"
                />
                <p className="text-sm text-gray-500 text-left -mt-1">Email cannot be changed</p>
              </div>

              {/* Save Button */}
              <div className="mt-8"> 
                <Button 
                  type="submit"
                  disabled={isLoading}
                  className="w-full font-medium bg-[#14171F] hover:bg-[#14171F] text-white rounded-xl py-3 text-sm"
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>

            {/* Sign Out Button */}
            <div className="pt-8">
              <Button 
                variant="ghost"
                onClick={handleSignOut}
                className="w-full font-medium text-sm text-red-600 hover:text-red-700 hover:bg-gray-100 rounded-xl py-3 border border-red-100"
              >
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Profile;